import { useQuery } from '@tanstack/react-query'
import { Brain, Sparkles, DollarSign, Zap, Target } from 'lucide-react'
import { reconciliationApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface AIMetricsDashboardProps {
  className?: string
  compact?: boolean
}

export function AIMetricsDashboard({ className, compact = false }: AIMetricsDashboardProps) {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['ai-metrics-summary'],
    queryFn: () => reconciliationApi.aiMetrics.getSummary(),
    refetchInterval: 60000, // Refresh every minute
  })

  if (isLoading) {
    return (
      <div className={cn('grid gap-4', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4', className)}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-4">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const totalCalls = (summary?.total_categorization_calls || 0) + 
                     (summary?.total_vendor_extraction_calls || 0) + 
                     (summary?.total_rule_suggestion_calls || 0)
  
  const costDollars = (summary?.total_estimated_cost_cents || 0) / 100
  const avgConfidence = summary?.avg_confidence_score || 0

  return (
    <div className={cn('space-y-4', className)}>
      {!compact && (
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">AI Processing Metrics</h3>
        </div>
      )}

      <div className={cn('grid gap-4', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4')}>
        <MetricCard
          title="Total AI Calls"
          value={totalCalls.toLocaleString()}
          icon={<Sparkles className="h-4 w-4" />}
          description="Categorization + Vendor + Rules"
          variant="primary"
        />
        <MetricCard
          title="Avg Confidence"
          value={`${(avgConfidence > 1 ? avgConfidence : avgConfidence * 100).toFixed(0)}%`}
          icon={<Target className="h-4 w-4" />}
          description="AI categorization accuracy"
          variant="success"
        />
        <MetricCard
          title="Tokens Used"
          value={formatTokens(summary?.total_input_tokens || 0, summary?.total_output_tokens || 0)}
          icon={<Zap className="h-4 w-4" />}
          description="Input + Output tokens"
          variant="info"
        />
        <MetricCard
          title="Estimated Cost"
          value={`$${costDollars.toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          description="Total AI processing cost"
          variant="warning"
        />
      </div>

      {!compact && summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">AI Call Breakdown</CardTitle>
            <CardDescription>Distribution of AI processing calls</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CallBreakdownBar
              label="Categorization"
              value={summary.total_categorization_calls}
              total={totalCalls}
              color="bg-blue-500"
            />
            <CallBreakdownBar
              label="Vendor Extraction"
              value={summary.total_vendor_extraction_calls}
              total={totalCalls}
              color="bg-green-500"
            />
            <CallBreakdownBar
              label="Rule Suggestions"
              value={summary.total_rule_suggestion_calls}
              total={totalCalls}
              color="bg-purple-500"
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface MetricCardProps {
  title: string
  value: string
  icon: React.ReactNode
  description: string
  variant: 'primary' | 'success' | 'warning' | 'info'
}

function MetricCard({ title, value, icon, description, variant }: MetricCardProps) {
  const variantStyles = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-1">
          <div className={cn('p-1.5 rounded-md', variantStyles[variant])}>
            {icon}
          </div>
          <span className="text-sm text-muted-foreground">{title}</span>
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  )
}

interface CallBreakdownBarProps {
  label: string
  value: number
  total: number
  color: string
}

function CallBreakdownBar({ label, value, total, color }: CallBreakdownBarProps) {
  const percentage = total > 0 ? (value / total) * 100 : 0

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="text-muted-foreground">{value.toLocaleString()} ({percentage.toFixed(0)}%)</span>
      </div>
      <Progress value={percentage} className={cn('h-2', `[&>div]:${color}`)} />
    </div>
  )
}

function formatTokens(input: number, output: number): string {
  const total = input + output
  if (total >= 1000000) {
    return `${(total / 1000000).toFixed(1)}M`
  }
  if (total >= 1000) {
    return `${(total / 1000).toFixed(1)}K`
  }
  return total.toString()
}

export default AIMetricsDashboard

